import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of, forkJoin, pipe } from 'rxjs';
import { map, take, switchMap, catchError } from 'rxjs/operators';
import {
  hasStripeSessionIdInUrl,
  isPartnerSignupPaymentReturn,
  isPartnerSignupReturnRoute,
  isSignupStripePaymentReturn,
  isStripeSubscriptionReturnUrl,
  restorePartnerAuthIfPresent,
} from '../modules/authentication/partner-signup/partner-signup-flow';
import { CommonService } from '../services/common.service';
import { resolveUnauthenticatedLoginPath } from '../modules/authentication/auth-login.routes';


@Injectable()
export class AuthGuard implements CanActivate {
  constructor( 
    private router: Router,
    private commonService : CommonService
    ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.commonService.isLoggedIn().pipe(
      take(1),
      switchMap((isLoggedIn: boolean) => {
        if (isLoggedIn) {
          // Redirect admin users away from user pages to admin dashboard
          const currentPath = state.url || '';
          const isBusinessAccount = localStorage.getItem('accountType') === 'B';

          // Pending RA/Trustee invite redirect is a personal-account-only feature —
          // admins, delegation admins, and business/advisor logins skip it entirely.
          if (this.commonService.isDelegationAdmin() && currentPath.includes('/delegation/')) {
            return of(true);
          }
          if (this.commonService.isAdmin()) {
            // Allow admin routes and authentication routes, but redirect from user routes
            if (!currentPath.includes('/admin/') &&
                !currentPath.includes('/clocr-admin') &&
                !currentPath.includes('/delegation/') &&
                !currentPath.startsWith('/login') &&
                !currentPath.startsWith('/advisor-login') &&
                !currentPath.startsWith('/signup') &&
                !currentPath.startsWith('/advisor-signup') &&
                !currentPath.startsWith('/partner-signup') &&
                !currentPath.startsWith('/onboard') &&
                !currentPath.startsWith('/verify-code') &&
                !currentPath.startsWith('/admin-verify-code') &&
                !currentPath.startsWith('/delegation-verify-code') &&
                currentPath !== '/') {
              // Only redirect if it's a user route (like /dashboard, /profile, etc.)
              // Don't redirect if it's an admin route (which will be handled by AdminAuthGuard)
              this.router.navigate(['/admin/disbursement']);
              return of(false);
            }
            return of(true);
          }
          if (isBusinessAccount) {
            return of(true);
          }
          return this.checkPendingInviteRedirect(state.url);
        } else {
          restorePartnerAuthIfPresent();

          const url = state.url || '';
          const stripeSessionId =
            next.queryParamMap.get('session_id') ||
            (hasStripeSessionIdInUrl(url) ? 'pending' : null);

          // Stripe return (incl. fresh browser): allow profile/subscription to finish sessionUpdate
          if (stripeSessionId) {
            return of(true);
          }

          if (localStorage.getItem('token')?.trim()) {
            return of(true);
          }

          const normalAwaitingPayment = isSignupStripePaymentReturn();
          const partnerAwaitingPayment = isPartnerSignupPaymentReturn();

          if (
            isStripeSubscriptionReturnUrl(url) &&
            (normalAwaitingPayment || partnerAwaitingPayment)
          ) {
            return of(true);
          }

          const loginPath = resolveUnauthenticatedLoginPath(url);

          // Partner-signup only — never send partner users to /signup
          if (isPartnerSignupReturnRoute()) {
            this.router.navigate(['/partner-signup']);
          } else if (url.startsWith('/profile')) {
            this.router.navigate([loginPath]);
          } else if (localStorage.hasOwnProperty('promoCode')) {
            const promoCode = JSON.parse(localStorage.getItem('promoCode'));
            if (promoCode.isValidPromoCode) {
              this.router.navigate([loginPath]);
            } else {
              this.router.navigate([loginPath]);
            }
          } else {
            this.router.navigate([loginPath]);
          }
          return of(false);

        }
      })
    );
    // this.router.navigate(['/login']);
    // return false;
  }

  // Redirects to /i-am-RA (RA/secret-key request pending) or /i-am-Trustee
  // (trustee nomination pending); no-op once accepted/rejected.
  private checkPendingInviteRedirect(currentUrl: string): Observable<boolean> {
    if (currentUrl.startsWith('/i-am-RA') || currentUrl.startsWith('/i-am-Trustee')) {
      return of(true);
    }

    return forkJoin({
      raRequests: this.commonService.getRecentGuardiansRequests().pipe(catchError(() => of([]))),
      secretKeyRequests: this.commonService.getRecentSecretKeysRequests().pipe(catchError(() => of([]))),
      trusteeRequests: this.commonService.getTrusteeDetails().pipe(catchError(() => of([]))),
    }).pipe(
      map(({ raRequests, secretKeyRequests, trusteeRequests }: any) => {
        const raList = raRequests?.data || raRequests || [];
        const secretList = secretKeyRequests?.data || secretKeyRequests || [];
        const hasPendingInvite = (Array.isArray(raList) && raList.length > 0) ||
          (Array.isArray(secretList) && secretList.length > 0);

        if (hasPendingInvite) {
          this.commonService.captureIamraBootstrap(raRequests, secretKeyRequests);
          this.router.navigate(['/i-am-RA']);
          return false;
        }

        // Unlike the RA endpoints, this one isn't pre-filtered by status.
        const trusteeList = trusteeRequests?.data || trusteeRequests || [];
        const hasPendingTrusteeInvite = Array.isArray(trusteeList) &&
          trusteeList.some((item: any) => item?.request_status === 'pending');

        if (hasPendingTrusteeInvite) {
          this.router.navigate(['/i-am-Trustee']);
          return false;
        }

        return true;
      }),
      catchError(() => of(true))
    );
  }
}